import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthApiError, MINIMUM_PASSWORD_LENGTH, requestPasswordReset, resetPassword } from '@/lib/auth/api';
import type { EmailChallenge } from '@/lib/auth/api';
import AuthHeader from './components/AuthHeader';
import AuthPasswordInput from './components/AuthPasswordInput';
import { AUTH_ICON_BTN, AUTH_INPUT, AUTH_PRIMARY_BTN, AUTH_TEXT_BTN, AUTH_TITLE } from './components/authStyles';

// One purpose: prove email ownership, choose a new password, then return to login.
export default function PasswordRecoveryPage() {
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState<EmailChallenge | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  function remember(value: EmailChallenge) {
    setChallenge(value);
    setCooldown(value.resendAfterSeconds);
    setCode('');
  }
  function failed(failure: unknown) {
    if (failure instanceof AuthApiError && failure.challenge) remember(failure.challenge);
    setError(failure instanceof Error ? failure.message : 'Request failed. Try again.');
  }

  async function sendCode() {
    if (busy || cooldown > 0) return;
    setBusy(true); setError('');
    try { remember(await requestPasswordReset(email.trim())); }
    catch (failure) { failed(failure); }
    finally { setBusy(false); }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!challenge) { await sendCode(); return; }
    if (!/^\d{6}$/.test(code) || password.length < MINIMUM_PASSWORD_LENGTH || password !== confirmPassword) {
      setError(`Enter the six-digit code and matching passwords of at least ${MINIMUM_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true); setError('');
    try {
      await resetPassword(challenge.challengeId, code, password);
      setPassword(''); setConfirmPassword(''); setDone(true);
    } catch (failure) { failed(failure); }
    finally { setBusy(false); }
  }

  return <div className="min-h-screen auth-background flex flex-col justify-between overflow-x-hidden text-neutral-900 antialiased">
    <AuthHeader subtitle="Enterprise Rewards & Loyalty" homeLabel="Home" />
    <main className="flex-1 flex items-center justify-center p-3 sm:p-5">
      <section className="w-full max-w-[380px]">
        <div className="text-center mb-6">
          <h1 className={AUTH_TITLE}>{challenge && !done ? 'Check your inbox' : 'Reset password'}</h1>
          {challenge && !done && <p role="status" className="mt-3 text-[15px] leading-6 text-neutral-800 break-words">
            If <span className="text-neutral-900">{email.trim()}</span> is eligible, we sent a code. Check your inbox and spam folder.
          </p>}
        </div>
        {error && <p role="alert" className="auth-field-message mb-3 text-sm">{error}</p>}
        {done ? <p role="status" className="text-center text-[15px] leading-6 text-neutral-800">Password updated. Previous sessions are signed out. Sign in with your new password.</p>
          : <form onSubmit={submit} className="space-y-4">
            {!challenge && <input type="email" required autoComplete="email" aria-label="Email address" placeholder="Email address" className={AUTH_INPUT} value={email}
              onChange={(event) => setEmail(event.target.value)} />}
            {challenge && <>
              <div className="relative">
                <label htmlFor="recovery-code" className="absolute -top-2 left-4 rounded bg-white px-1 text-xs leading-4 text-primary-600">Code</label>
                <input id="recovery-code" required autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} className={AUTH_INPUT} value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  onPaste={(event) => { event.preventDefault(); const value = event.clipboardData.getData('text').replace(/[\s-]/g, ''); if (/^\d{6}$/.test(value)) setCode(value); }} />
              </div>
              <AuthPasswordInput required autoComplete="new-password" minLength={MINIMUM_PASSWORD_LENGTH} maxLength={128} aria-label="New password"
                placeholder={`New password (at least ${MINIMUM_PASSWORD_LENGTH} characters)`} value={password} onChange={(event) => setPassword(event.target.value)} />
              <AuthPasswordInput required autoComplete="new-password" aria-label="Confirm password" placeholder="Confirm password"
                value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </>}
            <button disabled={busy} className={AUTH_PRIMARY_BTN}>
              {busy ? <i className="ri-loader-4-line animate-spin text-lg" role="status" aria-label="Please wait" /> : challenge ? 'Reset password' : 'Continue'}
            </button>
            {challenge && <>
              <button type="button" disabled={busy || cooldown > 0} onClick={sendCode}
                className="w-full h-10 text-[15px] text-neutral-800 hover:text-neutral-950 disabled:text-neutral-500 disabled:cursor-not-allowed cursor-pointer transition-colors">
                Resend email{cooldown > 0 ? ` (${cooldown}s)` : ''}
              </button>
              <button type="button" disabled={busy} onClick={() => { setChallenge(null); setCooldown(0); setCode(''); setError(''); }} className={AUTH_TEXT_BTN}>
                Change email
              </button>
            </>}
          </form>}
        <div className="flex justify-center mt-3">
          <Link to="/login" aria-label="Back to log in" title="Back to log in" className={AUTH_ICON_BTN}>
            <i className="ri-arrow-left-line text-xl leading-none" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
    <footer className="w-full px-5 py-6 flex items-center justify-center gap-3 text-xs text-neutral-700">
      <Link to="/terms" className="underline underline-offset-2 hover:text-neutral-900">Terms of Service</Link>
      <span className="h-3 w-px bg-neutral-500/50" />
      <Link to="/privacy" className="underline underline-offset-2 hover:text-neutral-900">Privacy Policy</Link>
    </footer>
  </div>;
}
